import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  Row,
  Col,
  ListGroup,
  Button,
  Image,
  Spinner,
  Alert,
  Form,
} from "react-bootstrap";
import api from "@/api/api.js";
import { formatDate } from "@/utils/formatDate";
import Swal from "sweetalert2";

const RequestTicketShow = () => {
  const { t } = useTranslation();
  const { requestTicketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [invoiceCompanyId, setInvoiceCompanyId] = useState("");

  const handleRequestInvoice = async () => {
    if (requestingInvoice) return;
    if (!invoiceCompanyId) {
      Swal.fire({
        icon: "warning",
        title: t("ui.selectAccount"),
      });
      return;
    }
    setRequestingInvoice(true);
    try {
      await api.post(`/request_tickets/${ticket.id}/request-invoice`, {
        companyId: invoiceCompanyId,
      });
      const { data } = await api.get(`/request_tickets/${ticket.id}`);
      setTicket(data);
      Swal.fire({
        icon: "success",
        title: "Solicitud enviada",
        text: "Se notificó al contador para emitir tu factura.",
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "No se pudo enviar la solicitud",
        text: e?.response?.data?.error || undefined,
      });
    } finally {
      setRequestingInvoice(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      const { data } = await api.get(
        `/request_tickets/${ticket.id}/invoice/download`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = ticket.invoiceOriginalName || "factura-folios.zip";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      Swal.fire({ icon: "error", title: "No se pudo descargar la factura" });
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const { data } = await api.get(`/request_tickets/${requestTicketId}`);
        setTicket(data);
        setError(null);
      } catch {
        setError("No se pudo cargar la solicitud");
        navigate("/request_tickets", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    if (requestTicketId) {
      fetchTicket();
    }
  }, [requestTicketId, navigate]);

  useEffect(() => {
    api
      .get("/companies", { params: { itemsPerPage: 100 } })
      .then(({ data }) => setCompanies(data["hydra:member"] || []))
      .catch(() => setCompanies([]));
  }, []);

  const isImage = (filename) => {
    return /\.(jpeg|jpg|gif|png|webp)$/i.test(filename);
  };

  useEffect(() => {
    let objectUrl;

    const fetchImage = async () => {
      if (!ticket || !ticket.voucherOriginalName) {
        setImageUrl(null);
        setImageError(null);
        return;
      }

      try {
        setImageError(null);

        const response = await api.get(`/request_tickets/${ticket.id}/image`, {
          responseType: "blob",
        });

        objectUrl = URL.createObjectURL(response.data);
        setImageUrl(objectUrl);
      } catch (e) {
        console.error("Error cargando el comprobante", e);
        setImageError("No se pudo cargar el comprobante");
        setImageUrl(null);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [ticket]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
        <p className="mt-2">Cargando detalles de la solicitud...</p>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <Alert variant="danger" className="mt-4">
        {error || "La solicitud no existe"}
      </Alert>
    );
  }

  return (
    <div className="container mt-4">
      <Button
        variant="outline-secondary"
        onClick={() => navigate(-1)}
        className="mb-3"
      >
        &larr; Volver
      </Button>

      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>Detalles de la solicitud</Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>ID:</strong> {ticket.id}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Paquete:</strong> {ticket.packageName}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Cantidad de folios:</strong> {ticket.quantityTickets}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Solicitado por:</strong>{" "}
                  {ticket.requestUser?.userProfile?.fullName}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Estado:</strong>{" "}
                  {ticket.approvalStatus === 1
                    ? "Aprobado"
                    : ticket.approvalStatus === 0
                      ? "Rechazado"
                      : "Pendiente"}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Fecha de creación:</strong> {ticket.createdAtAgo}
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mt-3 mt-md-0">
          <Card>
            <Card.Header>Comprobante de pagos</Card.Header>
            <Card.Body>
              {ticket.voucherPath ? (
                <>
                  <div className="mb-3">
                    <strong>Archivo:</strong> {ticket.voucherOriginalName}
                  </div>

                  {isImage(ticket.voucherOriginalName) ? (
                    <>
                      {imageError && (
                        <Alert variant="warning" className="mb-2">
                          {imageError}
                        </Alert>
                      )}

                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          fluid
                          rounded
                          className="mb-3"
                          style={{ maxHeight: "400px", objectFit: "contain" }}
                        />
                      ) : !imageError ? (
                        <div className="text-center text-muted">
                          Cargando imagen del comprobante...
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-center">
                      {imageError && (
                        <Alert variant="warning" className="mb-2">
                          {imageError}
                        </Alert>
                      )}
                      {imageUrl ? (
                        <Button
                          variant="primary"
                          href={imageUrl}
                          download={ticket.voucherOriginalName}
                        >
                          Descargar comprobante
                        </Button>
                      ) : !imageError ? (
                        <div className="text-center text-muted">
                          Cargando comprobante...
                        </div>
                      ) : null}
                    </div>
                  )}

                  <div className="mt-3 small text-muted">
                    <strong>Hash de verificación:</strong> {ticket.voucherHash}
                  </div>
                </>
              ) : (
                <div className="text-center text-muted">
                  No se encontró comprobante adjunto
                </div>
              )}
            </Card.Body>
          </Card>

          {ticket.approvedBy && (
            <Card className="mt-3">
              <Card.Header>Detalles de aprobación</Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>
                      {ticket.approvalStatus === 1 ? "Aprobado" : "No Aprobado"}{" "}
                      por:
                    </strong>{" "}
                    {ticket.approvedBy.userProfile.fullName}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Fecha de confirmación:</strong>{" "}
                    {formatDate(ticket.approvedDate)}
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {ticket.approvalStatus === 1 && (
            <Card className="mt-3">
              <Card.Header>Factura del paquete</Card.Header>
              <Card.Body className="d-flex flex-column gap-2">
                {ticket.invoiceRequestedAt ? (
                  <Alert variant="info" className="mb-0">
                    Factura solicitada el {formatDate(ticket.invoiceRequestedAt)}
                    {ticket.invoiceCompany?.name
                      ? ` a nombre de ${ticket.invoiceCompany.name}`
                      : ""}
                    .
                  </Alert>
                ) : (
                  <>
                    <Form.Group>
                      <Form.Label className="mb-1">
                        {t("ui.selectAccountLabel")}
                      </Form.Label>
                      <Form.Select
                        value={invoiceCompanyId}
                        onChange={(e) => setInvoiceCompanyId(e.target.value)}
                      >
                        <option value="">{t("ui.selectAccount")}</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                            {c.taxIdentification
                              ? ` — ${c.taxIdentification}`
                              : ""}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Button
                      variant="primary"
                      onClick={handleRequestInvoice}
                      disabled={requestingInvoice || !invoiceCompanyId}
                    >
                      {requestingInvoice ? "Enviando..." : "Solicita tu factura"}
                    </Button>
                  </>
                )}

                {ticket.invoiceOriginalName ? (
                  <Button
                    variant="success"
                    onClick={handleDownloadInvoice}
                    disabled={downloadingInvoice}
                  >
                    {downloadingInvoice
                      ? "Descargando..."
                      : "Descarga tu factura"}
                  </Button>
                ) : (
                  <small className="text-muted">
                    La descarga se activará cuando el contador suba tu factura.
                  </small>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default RequestTicketShow;
